import { httpClient } from '../http-client'
import type { UserMeetingsParams, UserMeetingsResponse } from '@/types/api'

/**
 * MeetingService - Gerencia reuniões e agendamentos
 */
class MeetingService {
  async getUserMeetings(userId: string, params?: UserMeetingsParams): Promise<UserMeetingsResponse> {
    const response = await httpClient.get(`/meetings/${userId}/meetings`, { params })
    return response.data
  }

  async getMeetingById(meetingId: string) {
    const response = await httpClient.get(`/meetings/${meetingId}`)
    return response.data
  }

  async createMeeting(data: any) {
    const response = await httpClient.post('/meetings', data)
    return response.data
  }

  async updateMeeting(meetingId: string, data: any) {
    const response = await httpClient.patch(`/meetings/${meetingId}`, data)
    return response.data
  }

  async deleteMeeting(meetingId: string) {
    const response = await httpClient.delete(`/meetings/${meetingId}`)
    return response.data
  }

  async cancelMeeting(meetingId: string) {
    const response = await httpClient.post(`/meetings/${meetingId}/cancel`)
    return response.data
  }

  async joinMeeting(meetingId: string) {
    const response = await httpClient.post(`/meetings/${meetingId}/join`)
    return response.data
  }

  async getZoomJoinContext(meetingId: string, identity?: { profileId?: string; email?: string; name?: string }) {
    const response = await httpClient.post(`/meetings/${meetingId}/zoom/join-context`, identity || {})
    return response.data
  }

  /**
   * URL de entrada identificada numa reunião Mentorfy Meeting (BigBlueButton).
   *
   * A URL é montada no servidor a partir do usuário autenticado — o cliente
   * não informa quem é. É isso que faz a presença ser atribuída ao aluno, já
   * que o BigBlueButton não coleta e-mail e a página pública de entrada só
   * pede um nome digitado.
   */
  async getBbbJoinContext(meetingId: string): Promise<{
    joinUrl: string
    role: 'moderator' | 'attendee'
    displayName: string
    meetingId: string
    friendlyId: string
  }> {
    const response = await httpClient.post(`/meetings/${meetingId}/bbb/join-context`)
    return response.data
  }

  /**
   * Records the click of the logged-in student (the bearer is sent only when
   * there is one) and returns the Google Meet URL. Public: works without login.
   */
  async joinGoogleMeet(meetingId: string): Promise<{ meetUrl: string | null; recorded: boolean }> {
    const response = await httpClient.post(`/meetings/${meetingId}/google-meet/join`)
    return response.data
  }

  async getZoomHostContext(meetingId: string, identity?: { profileId?: string; email?: string; name?: string }) {
    const response = await httpClient.post(`/meetings/${meetingId}/zoom/host-context`, identity || {})
    return response.data
  }

  async recordZoomAttendance(meetingId: string, data: Record<string, any>) {
    const response = await httpClient.post(`/meetings/${meetingId}/attendance`, data)
    return response.data
  }
}

export const meetingService = new MeetingService()
